import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import endpoints from "../../endpoint";
import { toast } from "react-toastify";

// Thunks
export const forgotPassword = createAsyncThunk(
  "user/forgotPassword",
  async (emailData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${endpoints.serverBaseUrl}api/user/forget-password`,
        { email: emailData.email }
      );
      toast.success(data.message);
      return data.message;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Something went wrong!";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "user/resetPassword",
  async ({ token, passwords }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${endpoints.serverBaseUrl}api/user/password/reset/${token}`,
        passwords
      );
      return data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Something went wrong!";
      return rejectWithValue(errorMessage);
    }
  }
);

export const login = createAsyncThunk(
  "user/login",
  async (userData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${endpoints.serverBaseUrl}api/user/login`,
        userData,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      sessionStorage.setItem("user", JSON.stringify(data?.user));
      sessionStorage.setItem("token", data.token);

      toast.success(data.message || "Login successful");
      return { user: data?.user, token: data?.token };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "An error occurred during login";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);
export const logoutThunk = createAsyncThunk(
  "user/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        endpoints.serverBaseUrl + "api/user/logout",
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("token");
      toast.success(response?.data?.message);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "An error occurred during Logout";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  loading: false,
  message: null,
  success: false,
  error: null,
  data: null,
};

// Slice
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.data = action.payload;
    },
    logout: (state) => {
      // Clear session/local storage
      sessionStorage.clear();
      localStorage.clear();
      // Clear Redux state for user
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.data = action.payload;
        state.message = "Login successful";
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logoutThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.data = [];
        state.message = action.payload.message;
      })
      .addCase(logoutThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearErrors, setUser, logout } = userSlice.actions;
export default userSlice.reducer;
